// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}


d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});


// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

stylePremium(CNNPolitics, CNNBusiness);

const margins = {
  top: 30,
  right: 80,
  bottom: 25,
  left: 40,
};

const chartData = [];
const minValue = 0;
const maxValue = 2000;
const minDomain = new Date(2000, 0, 1); // normalize to year 2000 to plot multiple years on same axis
const maxDomain = new Date(2000, 11, 31); // javascript months are zero-indexed
let latestData;
let currentYear;

d3.json('https://ix.cnn.io/data/tornado-reports/data.json').then((data) => {
  // Get current year and determine which years to show (current + previous 3 + average)
  currentYear = new Date().getFullYear().toString();
  const minYear = parseInt(currentYear) - 3;

  for (const key in data.data) {
    // Include: average, current year, and previous 3 years
    const isAverage = key === 'average';
    const isRecentYear = !isNaN(parseInt(key)) && parseInt(key) >= minYear;

    if (isAverage || isRecentYear) {
      chartData.push({
        year: key,
        data: data.data[key].map(d => ({
          date: new Date(2000, parseInt(d[0].split('-')[0]) - 1, parseInt(d[0].split('-')[1])),
          count: parseInt(d[1]),
        })),
      });
    }
  }

  const latestYear = chartData.filter(d => d.year === currentYear)[0];
  latestData = latestYear.data[latestYear.data.length - 1];

  const localMax = d3.max(chartData, d => d3.max(d.data, e => e.count));
  if (localMax > maxValue) {
    maxValue = localMax;
  }

  // const updateTime = d3.timeParse('%Y-%m-%dT%H:%M:%S%Z')(data.timestamp);

  // d3.select('span#timestamp')
  //   .text(`${d3.timeFormat(' %B %-d, %Y at %-I %p')(updateTime)}`);

  function toEasternTime(date) {
    const options = {
      timeZone: 'America/New_York',
      hour12: true,
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
    };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(date);

    // Reconstruct the formatted date string and add "ET" at the end
    const formattedDate = parts.map(({ type, value }) => value).join('');
    return `${formattedDate} ET`;
  }

  // Parse the timestamp
  const updateTime = new Date(data.timestamp);

  // Convert to ET and format the timestamp
  const formattedTimeET = toEasternTime(updateTime);

  // Update the HTML element with the formatted timestamp
  d3.select('span#timestamp')
    .text(formattedTimeET.replace('PM', ' p.m.').replace('AM', ' a.m.'));

  // Set dynamic dek
  d3.select('.dek')
    .text(`The number of tornado reports recorded by the National Weather Service in ${currentYear} compared with the previous three years and the 15-year average from 2006–2020.`);

  draw();
  window.onresize = draw;
});


function draw() {
  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(4)
    .tickFormat(d => d3.timeFormat('%b')(d));

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .tickSize(-chartWidth)
    .ticks(5)
    .tickFormat(d => d3.format(',')(d));

  const path = d3.line()
    .curve(d3.curveLinear)
    .x(d => xScale(d.date))
    .y(d => yScale(d.count));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  d3.select('.grid.y .domain').remove();

  const line = g.selectAll('.trend')
    .data(chartData)
    .enter()
    .append('g')
    .attr('class', d => `trend trend-${d.year}${d.year === currentYear ? ' trend-current' : ''}`);

  line.append('path')
    .datum(d => d.data)
    .attr('d', path);


  const annotation = g.append('g')
    .attr('class', 'annotation-current');

  annotation.append('circle')
    .attr('cx', xScale(latestData.date))
    .attr('cy', yScale(latestData.count))
    .attr('r', '4')
    .attr('fill', '#c00')
    .attr('stroke', 'none');

  annotation.append('line')
    .attr('x1', xScale(latestData.date))
    .attr('x2', xScale(latestData.date))
    .attr('y1', yScale(latestData.count) - 5)
    .attr('y2', yScale(1945))
    .attr('class', 'indicator-line');

  const annotationText = annotation.append('g')
    .attr('transform', `translate(${[xScale(latestData.date) + 5, yScale(1900)]})`);

  annotationText.append('text')
    .attr('dy', '0')
    .attr('class', 'annotation-label')
    .text(currentYear);

  annotationText.append('text')
    .attr('dy', '1.2em')
    .attr('class', 'annotation-value')
    .text(`${d3.format(',')(latestData.count)} ${latestData.count === 1 ? 'report' : 'reports'}`);

  annotationText.append('text')
    .attr('class', 'annotation-value-line2')
    .text(`as of ${d3.timeFormat('%b %-d')(latestData.date)}`)
    .attr('dy', '2.6em');

  // Build array of all end-of-line labels for collision avoidance
  const endLabels = [];

  // Add previous year labels
  chartData
    .filter(d => d.year !== 'average' && d.year !== currentYear)
    .forEach((d) => {
      const lastPoint = d.data[d.data.length - 1];
      endLabels.push({
        year: d.year,
        x: xScale(lastPoint.date) + 5,
        y: yScale(lastPoint.count),
        naturalY: yScale(lastPoint.count),
        isAverage: false,
      });
    });

  // Add average label
  const avgData = chartData.find(d => d.year === 'average');
  if (avgData) {
    const lastAvgPoint = avgData.data[avgData.data.length - 1];
    endLabels.push({
      year: 'average',
      x: xScale(new Date(2000, 11, 31)) + 5,
      y: yScale(lastAvgPoint.count),
      naturalY: yScale(lastAvgPoint.count),
      isAverage: true,
    });
  }

  // Sort by y position (top to bottom in pixel space, so ascending)
  endLabels.sort((a, b) => a.y - b.y);

  // Collision avoidance - minimum spacing between labels
  const minSpacing = 16;
  for (let i = 1; i < endLabels.length; i++) {
    const prev = endLabels[i - 1];
    const curr = endLabels[i];
    if (curr.y - prev.y < minSpacing) {
      curr.y = prev.y + minSpacing;
    }
  }

  // Render labels with adjusted positions
  endLabels.forEach((label) => {
    const annotationGroup = g.append('g')
      .attr('class', `annotation-prev annotation-${label.year}`)
      .attr('transform', `translate(${[label.x, label.y]})`);

    if (label.isAverage) {
      annotationGroup.append('text')
        .attr('class', 'annotation-prev-text')
        .attr('text-anchor', 'start')
        .text('15-year');

      annotationGroup.append('text')
        .attr('class', 'annotation-prev-text')
        .attr('text-anchor', 'start')
        .attr('dy', '1.2em')
        .text('average');
    } else {
      annotationGroup.append('text')
        .attr('class', 'annotation-prev-text-year')
        .text(label.year);
    }
  });


  pymChild.sendHeight();
}
